import React, { useCallback, useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import Page from "../../types/Page";
import Layout from "../../components/Layout";
import PageHeader from "../../components/PageHeader";
import {
  Button,
  Card,
  Col,
  Form,
  FormCheck,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Feature, Icon } from "@lomarengas/design-system";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useAuthUser } from "../../features/auth/authHooks";
import {
  AccommodationListItem,
  AccommodationQueryFilters,
  getAccommodationsAsync,
  selectAccommodationItems,
  selectAccommodationsLoadingStatus,
} from "../AccommodationsPage/accommodationsSlice";
import { InitialAllAccommodationsPaginationParameters } from "../../types/pagination.types";
import { DateRange } from "react-day-picker";
import { selectInputErrors } from "../../features/errors/errorsSlice";
import DatePicker from "react-datepicker";
import { LOADING_STATUS } from "../../common/constants";
import {
  CustomerFeedBackListOrderByDirectionOptions,
  CustomerFeedBackListOrderByOptions,
  getFeedbacksAsync,
} from "../FeedBacks/FeedBacksSlice";
import { PaginationComponent } from "../../components/PaginationComponent";
import {
  getSettlementReport,
  selectSettlementReport,
  selectSettlementReportLoadingStatus,
  SettlementReportItem,
  SettlementReportItemLine,
  SettlementReportRequest,
} from "./ReportsSlice";
import { forEach } from "react-bootstrap/ElementChildren";
import {
  bookingTypeEnumToTranslatedShortHandText,
  bookingTypeEnumToTranslatedText,
  dateToDateOnlyString,
  dateToDateOnlyStringTwoYear,
  formattedAmount,
  formattedAmountWithEuroCurrency,
  showOrHideElementsByClassName,
} from "../../common/misc";
import {
  getAccountDetailsFromDbAsync,
  selectOwnerInfo,
} from "../UserPage/userSlice";
import FormCheckInput from "react-bootstrap/FormCheckInput";
import { format } from "date-fns";
import LocalizedDatePicker from "../../components/DatePicker/LocalizedDatePicker";
import { BookingType } from "../BookingsPage/bookingsSlice";

interface SettlementReportVatItem {
  vatP: number;
  vat: number;
  SumWithVat: number;
}

interface SettlementReportVats {
  settlementReportVatItems: SettlementReportVatItem[];
}

const ReportsPageType: Page = {
  ActiveTab: "reports",
  DisplayName: "Tilitysraportti",

  LinkUrl: "/reports/settlementreport",
  Path: "/reports/settlementreport",
  RouteName: "reports/settlementreport",
};

const SettlementReportPage = () => {
  let settlementReportVats: SettlementReportVats = {
    settlementReportVatItems: [],
  };

  const contentRef = useRef(null);

  const params = useParams();
  const dispatch = useAppDispatch();
  const { ownerAccountId } = useAuthUser();
  const { isOwnerUser } = useAuthUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const ownerInfo = useAppSelector(selectOwnerInfo);
  const [accommodationFiltering, setAccommodationFiltering] =
    useState<AccommodationQueryFilters>({
      PageNumber: InitialAllAccommodationsPaginationParameters.PageNumber,
      PageSize: InitialAllAccommodationsPaginationParameters.PageSize,
      filterCriteria: {
        showDeleted: false,
        searchString: undefined,
        accommodationNumber: undefined,
        novelty: undefined,
        showInWebShop: undefined,
        showInOwnerPage: undefined,
        accommodationGroupId: undefined,
        accountId: ownerAccountId,
      },
    });

  // Get today's date
  let today = new Date();
  let oneMonthAgo = new Date();

  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const [hideOwnerOwnBookings, setHideOwnerOwnBookings] =
    useState<boolean>(false);
  const bookingsStartDateFromParams = searchParams.get("bookingsStartDate");
  const bookingsEndDateFromParams = searchParams.get("bookingsEndDate");

  if (bookingsStartDateFromParams != null) {
    oneMonthAgo = new Date(bookingsStartDateFromParams);
  }
  if (bookingsEndDateFromParams != null) {
    today = new Date(bookingsEndDateFromParams);
  }
  const noBookingsAfterEndDateFromParams = searchParams.get(
    "noBookingsAfterEndDate",
  );
  const accommodationDropdownSelectedAccommodationFromParam = searchParams.get(
    "accommodationDropdownSelectedAccommodation",
  );
  // Create a date object for tomorrow
  // const tomorrow = new Date();
  // tomorrow.setDate(today.getDate() + 1);
  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>({
      from: oneMonthAgo,
      to: today,
    });
  const [startDate, setStartDate] = useState(oneMonthAgo);
  const [endDate, setEndDate] = useState(today);
  const [
    accommodationDropdownSelectedAccommodation,
    setAccommodationDropdownSelectedAccommodation,
  ] = useState<string>(
    accommodationDropdownSelectedAccommodationFromParam != null
      ? accommodationDropdownSelectedAccommodationFromParam
      : undefined,
  );
  const [noBookingsAfterEndDate, setNoBookingsAfterEndDate] = useState(
    noBookingsAfterEndDateFromParams == "true",
  );
  const inputErrors = useAppSelector(selectInputErrors);
  const accommodations = useAppSelector(selectAccommodationItems);
  const settlementReport = useAppSelector(selectSettlementReport);

  const accommodationsLoadingStatus = useAppSelector(
    selectAccommodationsLoadingStatus,
  );
  const settlementReportLoadingStatus = useAppSelector(
    selectSettlementReportLoadingStatus,
  );
  const initAccommodationsFetch = useCallback(() => {
    dispatch(getAccommodationsAsync(accommodationFiltering));
  }, [dispatch, ownerAccountId]);

  const ownerInfoFetch = useCallback(() => {
    dispatch(getAccountDetailsFromDbAsync(ownerAccountId));
  }, [dispatch, ownerAccountId]);
  useEffect(() => {
    initAccommodationsFetch();
    ownerInfoFetch();
  }, [initAccommodationsFetch, ownerAccountId]);

  const accommodationDropdownChanged = (e: any) => {
    if (e.target.value != "0") {
      let selectedAccommodation = accommodations.find(
        (accommodation) => accommodation.id == e.target.value,
      );
      setAccommodationDropdownSelectedAccommodation(selectedAccommodation.id);
    } else {
      setAccommodationDropdownSelectedAccommodation(undefined);
    }
  };
  const setAccommodationDropdownDateRange = (
    startDateSelected: Date,
    endDateSelected: Date,
  ) => {
    let selectedRange = dayPickerSelectedRange;
    if (startDateSelected != undefined) {
      selectedRange.from = startDateSelected;
      setStartDate(startDateSelected);
      // Move enddate, if selected startdate is greater than endDate
      if (endDate < startDateSelected) {
        setEndDate(startDateSelected);
      }
    }
    if (endDateSelected != undefined) {
      selectedRange.to = endDateSelected;
      setEndDate(endDateSelected);
    }
    setDayPickerSelectedRange(selectedRange);
  };
  const noBookingsAfterEndDateHandler = () => {
    setNoBookingsAfterEndDate(!noBookingsAfterEndDate);
  };
  const doSearch = () => {
    const reportRequest: SettlementReportRequest = {
      ownerId: ownerAccountId,
      bookingsStartDate: startDate,
      bookingsEndDate: endDate,
      isOwnerUser: isOwnerUser,
      noBookingsAfterEndDate: noBookingsAfterEndDate,
    };

    searchParams.set(
      "bookingsStartDate",
      format(reportRequest.bookingsStartDate, "yyyy-MM-dd"),
    );
    searchParams.set(
      "bookingsEndDate",
      format(reportRequest.bookingsEndDate, "yyyy-MM-dd"),
    );

    searchParams.set(
      "noBookingsAfterEndDate",
      reportRequest.noBookingsAfterEndDate.toString(),
    );
    if (accommodationDropdownSelectedAccommodation != null) {
      reportRequest.accommodationId =
        accommodationDropdownSelectedAccommodation;
      searchParams.set(
        "accommodationDropdownSelectedAccommodation",
        accommodationDropdownSelectedAccommodation,
      );
    } else {
      searchParams.delete("accommodationDropdownSelectedAccommodation");
    }
    setSearchParams(searchParams);
    dispatch(getSettlementReport(reportRequest));
  };
  const createLinkToBooking = (reportLine: SettlementReportItemLine) => {
    let link =
      "/bookings/booking/" +
      reportLine.bookingId +
      "?bookingNumber=" +
      reportLine.bookingNumber +
      "&accommodationId=" +
      reportLine.accommodationId;
    return link;
  };
  const doPdf = () => {
    const pdfHeader = document.getElementById("reportPdfHeader");
    if (pdfHeader) {
      // Make the pdfHeader visible
      pdfHeader.style.display = "block";
      document.body.classList.add("pdf-mode");
    }
    //Hide the hyperlinks
    showOrHideElementsByClassName("bookingNumberLink", false);
    //Show the texts
    showOrHideElementsByClassName("bookingNumberText", true);

    const options = {
      filename: "Tilitysraportti_" + dateToDateOnlyString(new Date()) + ".pdf",
      margin: 0.2,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: {
        unit: "in",
        orientation: "portrait",
        putTotalPages: true,
        compress: true,
      },
      autoPaging: "text",
      pagebreak: {
        avoid: "tr",
      },
    };

    //const convertToPdf = () => {
    const content = contentRef.current;
    html2pdf()
      .set(options)
      .from(content)
      .toPdf()
      .get("pdf")
      .then(function (pdf) {
        var totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(100);
          pdf.text(
            "Sivu " + i + " / " + totalPages,
            9.5,
            pdf.internal.pageSize.getHeight() - 8,
          );
        }
        // Hide the header again after PDF generation
        if (pdfHeader) {
          pdfHeader.style.display = "none";
          document.body.classList.remove("pdf-mode");
        }
        //Show the hyperlinks
        showOrHideElementsByClassName("bookingNumberLink", true);
        //Hide the texts
        showOrHideElementsByClassName("bookingNumberText", false);
      })
      .save()
      .catch((error) => {
        console.error("Error generating PDF:", error);
        // Ensure the header is hidden in case of an error
        if (pdfHeader) {
          pdfHeader.style.display = "none";
        }
      });
  };

  let totalSettlementValue = 0;
  let totalSettlementVatValue = 0;
  const renderReportLine = (
    reportLine: SettlementReportItemLine,
    extraBooking: boolean,
  ) => {
    if (
      reportLine.ownerPurchasePriceVatP > 0 &&
      reportLine.ownerPurchasePrice > 0
    ) {
      const existingVatItem =
        settlementReportVats.settlementReportVatItems.find(
          (item) => item.vatP === reportLine.ownerPurchasePriceVatP,
        );
      if (existingVatItem) {
        // If it exists, add to the existing item's prices
        existingVatItem.vat += reportLine.ownerPurchasePriceVat; // Add your value here
        existingVatItem.SumWithVat += reportLine.ownerPurchasePrice; // Add your value here
      } else {
        // If it doesn't exist, add a new item with zero prices
        settlementReportVats.settlementReportVatItems.push({
          vatP: reportLine.ownerPurchasePriceVatP,
          vat: reportLine.ownerPurchasePriceVat,
          SumWithVat: reportLine.ownerPurchasePrice,
        });
      }
    }
    totalSettlementVatValue += reportLine.ownerPurchasePriceVat;
    totalSettlementValue += reportLine.ownerPurchasePrice;
    return (
      <tr key={reportLine.bookingId + reportLine.productName}>
        <td className="d-print-none">
          {extraBooking && "* "}
          {extraBooking && reportLine.bookingNumber}
          {reportLine.isMainProduct && (
            <>
              <div className="bookingNumberLink">
                <Link to={createLinkToBooking(reportLine)}>
                  {" "}
                  {reportLine.bookingNumber}
                </Link>
              </div>
              <div className="bookingNumberText" style={{ display: "none" }}>
                {reportLine.bookingNumber}{" "}
              </div>
            </>
          )}
        </td>
        <td>
          {!reportLine.isMainProduct && "+ "}{" "}
          {reportLine.productNumber + " " + reportLine.productName}
        </td>
        <td>{dateToDateOnlyStringTwoYear(reportLine.startDate)}</td>
        <td>{dateToDateOnlyStringTwoYear(reportLine.endDate)}</td>
        <td>
          <span>
            {reportLine.bookingType == 5 &&
              bookingTypeEnumToTranslatedText(reportLine.bookingType)}
            {reportLine.payerFirstName} {reportLine.payerLastName}
          </span>
        </td>
        <td>{formattedAmount(reportLine.ownerPurchasePrice)}</td>
        <td>{reportLine.ownerPurchasePriceVatP}</td>
        <td>{formattedAmount(reportLine.ownerPurchasePriceVat)}</td>
      </tr>
    );
  };

  const renderReportItem = (
    reportItem: SettlementReportItem,
    extraBooking: boolean,
  ) => {
    return (
      <>
        {/* Render the main report lines */}
        {reportItem.settlementReportlines
          .filter(
            (reportLine) =>
              !hideOwnerOwnBookings ||
              (reportLine.bookingType !== BookingType.OwnerOwn &&
                reportLine.bookingType !== BookingType.TemporaryDisabled),
          )
          .map((reportLine) => renderReportLine(reportLine, extraBooking))}

        {/* Render the extra booking report items recursively */}
        {reportItem.extraBookingSettlementReportItems?.map(
          (extraReportItem, index) => (
            <React.Fragment key={index}>
              {renderReportItem(extraReportItem, true)}
            </React.Fragment>
          ),
        )}
      </>
    );
  };

  const renderReport = () => {
    settlementReportVats.settlementReportVatItems = [];
    return settlementReport.settlementReportItems.map((reportItem, index) => (
      <React.Fragment key={index}>
        {renderReportItem(reportItem, false)}
      </React.Fragment>
    ));
  };
  const renderVats = () => {
    return settlementReportVats.settlementReportVatItems
      .slice() // make a shallow copy so you don’t mutate original
      .sort((a, b) => a.vatP - b.vatP) // numeric ascending
      .map((vatItem, index) => (
        <tr key={index}>
          <td className="px-8">{vatItem.vatP} %</td>
          <td>
            {formattedAmountWithEuroCurrency(vatItem.SumWithVat - vatItem.vat)}
          </td>
          <td className="text-nowrap">
            {formattedAmountWithEuroCurrency(vatItem.vat)}
          </td>
          <td>{formattedAmountWithEuroCurrency(vatItem.SumWithVat)}</td>
        </tr>
      ));
  };
  return (
    <div className={ReportsPageType.RouteName}>
      <Layout CurrentPage={ReportsPageType}>
        <PageHeader
          Title={ReportsPageType.DisplayName}
          SubTitle={ReportsPageType.DisplayParentName}
          Back={false}
          Icon="graph-up-arrow"
          Print={true}
        ></PageHeader>
        <div>
          <Row className="d-print-none gy-16">
            <Col xl="4">
              <Form.Label htmlFor="selectCabin">Kohde</Form.Label>
              <Form.Select
                aria-label="Kohde"
                id="selectCabin"
                value={accommodationDropdownSelectedAccommodation ?? 0} // controlled selection
                onChange={(e) => accommodationDropdownChanged(e)}
              >
                <option value={0}>Näytä kaikki kohteet</option>
                {accommodations
                  .slice()
                  .sort(
                    (a, b) =>
                      (Number(a.accommodationNumber) ?? 0) -
                      (Number(b.accommodationNumber) ?? 0),
                  )
                  .map((accommodation) => (
                    <option key={accommodation.id} value={accommodation.id}>
                      {accommodation.name}, {accommodation.accommodationNumber}
                    </option>
                  ))}
              </Form.Select>
              <div>
                <div className="form-check mt-16">
                  <input
                    checked={hideOwnerOwnBookings}
                    onChange={() =>
                      setHideOwnerOwnBookings(!hideOwnerOwnBookings)
                    }
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="flexCheckDefault"
                  />
                  <Form.Label
                    className="form-check-label"
                    htmlFor="flexCheckDefault"
                  >
                    Piilota oma varaukset
                  </Form.Label>
                </div>
              </div>
            </Col>
            <Col md="7" xl="5">
              <Form.Label htmlFor="selectStart">Valitse ajankohta</Form.Label>
              <div className="d-flex  gap-8 align-items-center">
                <div className="w-100 position-relative">
                  <LocalizedDatePicker
                    selected={startDate}
                    onChange={(date) =>
                      setAccommodationDropdownDateRange(date, undefined)
                    }
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    className="form-control"
                    dateFormat={"dd.MM.yyyy"}
                    showWeekNumbers
                  />
                  <Button
                    variant="link"
                    className="pe-none position-absolute end-0 top-0"
                    id="button-addon2"
                  >
                    <span className="bi bi-calendar2-event"></span>
                  </Button>
                </div>
                <span className="bi bi-arrow-right"></span>
                <div className="w-100 position-relative">
                  <LocalizedDatePicker
                    selected={endDate}
                    onChange={(date) =>
                      setAccommodationDropdownDateRange(undefined, date)
                    }
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    className="form-control"
                    dateFormat={"dd.MM.yyyy"}
                    showWeekNumbers
                  />
                  <Button
                    variant="link"
                    className="pe-none position-absolute end-0 top-0"
                    id="button-addon2"
                  >
                    <span className="bi bi-calendar2-event"></span>
                  </Button>
                </div>
              </div>
              <div className="form-check mt-16">
                <input
                  checked={noBookingsAfterEndDate}
                  onChange={noBookingsAfterEndDateHandler}
                  className="form-check-input"
                  type="checkbox"
                  value=""
                  id="flexCheckDefault"
                />
                <label className="form-check-label" htmlFor="flexCheckDefault">
                  Rajaa varaukset päättymispäivän mukaan
                </label>
              </div>
            </Col>
            <Col md="5" xl="3">
              <label className="form-label d-none d-md-block">&nbsp;</label>
              {
                <Button
                  onClick={() => doSearch()}
                  variant="primary"
                  className="w-100 text-nowrap"
                >
                  Tee raportti
                </Button>
              }
            </Col>
          </Row>

          {settlementReportLoadingStatus != LOADING_STATUS.IDLE && (
            <div className="d-flex gap-16 align-items-center mt-24">
              <Spinner animation="border" />
              Ladataan saatavilla olevia kohteita...
            </div>
          )}
          {settlementReportLoadingStatus == LOADING_STATUS.IDLE && (
            <div ref={contentRef}>
              <div id="reportPdfHeader" style={{ display: "none" }}>
                <Row>
                  <Col>
                    <h4 className="mb-16">LOMARENGAS OY</h4>
                  </Col>
                  <Col>
                    <h6>TILITYSRAPORTTI {dateToDateOnlyString(new Date())}</h6>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="mb-24">
                      Siltasaarenkatu 8-10
                      <br />
                      FI-00530 Helsinki
                      <br />
                      Puh 0306 502 502
                    </p>
                  </Col>
                  <Col>
                    <p className="mb-24">
                      {ownerInfo != null &&
                        ownerInfo.firstName + " " + ownerInfo.lastName}
                      <br />
                      {ownerInfo != null && ownerInfo.postStreetAddress}
                      <br />
                      {ownerInfo != null &&
                        ownerInfo.postCode + " " + ownerInfo.postCity}
                    </p>
                  </Col>
                </Row>
                <p>
                  <h6>
                    Kausi: {dateToDateOnlyString(dayPickerSelectedRange.from)} -{" "}
                    {dateToDateOnlyString(dayPickerSelectedRange.to)}
                  </h6>
                </p>
              </div>
              {settlementReport.settlementReportItems.length > 0 && (
                <>
                  <Button
                    onClick={() => doPdf()}
                    variant="outline-blue"
                    className="float-end  text-nowrap my-24 d-print-none"
                  >
                    <Icon Name="file-pdf" CssClass="me-8" />
                    Tulosta Pdf
                  </Button>
                  <table className="table mt-16 mb-0 table-printable">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          style={{ minWidth: "80px", maxWidth: "100px" }}
                          className="d-print-none"
                        >
                          Varaus
                        </th>
                        <th scope="col">Kohde</th>
                        <th scope="col">Alku</th>
                        <th scope="col">Loppu</th>
                        <th scope="col">Asiakas</th>
                        <th scope="col">Tilitys</th>
                        <th scope="col">Alv</th>
                        <th scope="col">Alv osuus</th>
                      </tr>
                    </thead>
                    <tbody>{renderReport()}</tbody>
                    <tfoot>
                      <tr>
                        <th className="d-print-none" scope="col"></th>
                        <th scope="col"></th>
                        <th scope="col"></th>
                        <th scope="col"></th>
                        <th scope="col">Yhteensä</th>
                        <th scope="col">
                          {formattedAmountWithEuroCurrency(
                            totalSettlementValue,
                          )}
                        </th>
                        <th scope="col"></th>
                        <th scope="col">
                          {formattedAmountWithEuroCurrency(
                            totalSettlementVatValue,
                          )}
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                  {settlementReportVats.settlementReportVatItems.length > 0 && (
                    <>
                      <br />
                      <div
                        id="reportVatSpecificationDiv"
                        style={{ width: "500px" }}
                      >
                        <h5>Arvonlisäveroerittely</h5>
                        <table className="table">
                          <thead>
                            <tr>
                              <th scope="col">Alv</th>
                              <th scope="col">Veroton</th>
                              <th scope="col">Vero</th>
                              <th scope="col">Yhteensä</th>
                            </tr>
                          </thead>
                          <tbody>{renderVats()}</tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default SettlementReportPage;
